import { createFileRoute, type LinkProps, useNavigate } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import useResponsive from 'ahooks/es/useResponsive';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReportError, useQuery } from '@furality/frontend-stack';
import fox from '#/api/fox.ts';
import style from '@/components/_explore/Explore.module.scss';
import ExploreFadeOut from '@/components/_explore/ExploreFadeOut.tsx';
import ExploreLocations from '@/components/_explore/ExploreLocations.tsx';
import ExplorePage from '@/components/_explore/ExplorePage.tsx';
import ExploreView from '@/components/_explore/ExploreView.tsx';
import ExploreMap from '@/components/_explore/map/ExploreMap.tsx';
import MapElements from '@/components/_explore/map/MapElements.tsx';
import { classifyEvent, type ExploreLocationData } from '@/components/_explore/map/models.tsx';
import useExploreControls from '@/components/_explore/useExploreControls.tsx';
import { MapLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import PageDisabled from '@/components/PageDisabled.tsx';
import { CON_NAME } from '@/lib/constants';
import objectArrayFromEntries from '@/lib/helpers/objectArrayFromEntries.ts';
import useFlag from '@/lib/hooks/useFlag.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';

const imageAssetUrl =
  'https://cdn.furality.org/image/f9/portal/explore/desktop/explore_map_composite_fireworks_unlocked.png';
const lockedFireworksImageAssetUrl =
  'https://cdn.furality.org/image/f9/portal/explore/desktop/explore_map_composite_fireworks_locked.png';

const Explore = () => {
  const [location, _setLocation] = useState<ExploreLocationData>();
  const updateLocation = (loc: ExploreLocationData) => {
    _setLocation(loc);
    redirectToWithFade(loc.linkProps);
  };
  const [fadingOut, setFadingOut] = useState(false);
  const url = useRef<LinkProps>(null);
  const navigate = useNavigate();
  const { xs, lg, md, sm } = useResponsive();
  const isDesktop = lg;
  const isTablet = md && !isDesktop;
  const isMobile = (sm || xs) && !isTablet && !isDesktop;
  const { resetView, centerViewToElement, x, y, zoom } = useExploreControls();
  const { t } = useTranslation(['explore']);

  // Events happening now (according to schedule) grouped by where they should show up
  const now = new Date().getTime() / 1000;
  const events = useQuery({
    queryKey: [QUERY_KEYS.Event],
    queryFn: async () => {
      const response = await fox.value.eventFindAll();
      switch (response.status) {
        case 200:
          return response.data;
        default:
          throw new ReportError(response);
      }
    },
  });
  const currentEvents = objectArrayFromEntries(
    (
      events.data?.data.filter(
        ({ start, end }) => new Date(end).getTime() > now && now >= new Date(start).getTime(),
      ) ?? []
    ).flatMap((event) => {
      const classification = classifyEvent(event);
      return classification === undefined ? [] : [[classification, event]];
    }),
  );
  currentEvents.lobby = [];
  currentEvents.games = [];
  currentEvents.dealers = [];
  currentEvents.fireworks = [];

  const handleLocationClick = useCallback(
    (exploreLocation: ExploreLocationData) => {
      updateLocation(exploreLocation);

      if (isDesktop) {
        return; // don't zoom in on desktop.
      }

      const element = document.getElementById(exploreLocation.id);

      if (!element) {
        return;
      }

      centerViewToElement(element as HTMLDivElement);
    },
    [centerViewToElement, isDesktop],
  );

  const redirectToWithFade = useCallback((destination: LinkProps) => {
    setFadingOut(true);
    url.current = destination;
  }, []);

  const handleResize = useCallback(() => {
    if (!isDesktop) {
      resetView();
    }
  }, [resetView, isDesktop]);

  useEffect(() => {
    if (!isDesktop) {
      resetView();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  /* The following state, callback and effect are needed for Chrome to
   * trigger a page re-render and change videoAssetUrl;
   * Firefox works without this spaghettic code.
   */
  const [windowWidth, setWindowWidth] = useState(-1);
  if (windowWidth < 0) {
    setWindowWidth(document.body.getBoundingClientRect().width);
  }

  const getNewWindowWidth = useCallback(() => {
    const newWindowWidth = document.body.getBoundingClientRect().width;
    if (windowWidth !== newWindowWidth) {
      setWindowWidth(newWindowWidth);
    }
  }, [windowWidth]);

  useEffect(() => {
    window.addEventListener('resize', getNewWindowWidth);
    return () => window.removeEventListener('resize', getNewWindowWidth);
  });

  const featuresReady = useFlagsStatus().flagsReady;
  const enableExplore = useFlag('explore');
  const enableExploreMap = useFlag('explore.map');
  const enableFireworks = useFlag('explore.fireworks');

  if (!featuresReady) {
    return <PageLoading />;
  }
  if (!enableExplore) {
    return <PageDisabled absolutelyCentered />;
  }

  return (
    <div
      className="animate__animated animate__fadeIn d-flex justify-content-center"
      style={{
        display: 'contents',
      }}
    >
      {!isMobile && enableExploreMap && (
        <ExploreFadeOut
          active={fadingOut}
          onFadeOutEnd={() => url.current && void navigate(url.current)}
        >
          <ExploreMap
            // CON-SPECIFIC: event explore map image
            imageAssetUrl={enableFireworks ? imageAssetUrl : lockedFireworksImageAssetUrl}
            // videoAssetUrl={videoAssetUrl}
            // imageOverlay={imageOverlay}
          >
            <MapElements
              onLocationClick={handleLocationClick}
              currentEventsByLocationId={currentEvents}
            />
          </ExploreMap>
        </ExploreFadeOut>
      )}

      {(isMobile || !enableExploreMap) && (
        <ExplorePage location={location}>
          <div className={clsx('mt-4', style['titleContainer'])}>
            <h1 className="text-white">Furality {CON_NAME}</h1>
            <p className="text-white">{t('explore:intro.choose_location')}</p>
          </div>

          <ExploreView x={x} y={y} zoom={zoom}>
            <ExploreLocations
              onLocationClick={handleLocationClick}
              currentEventsByLocationId={currentEvents}
              marginTop={30}
            />
          </ExploreView>
        </ExplorePage>
      )}
    </div>
  );
};

// biome-ignore lint/style/useComponentExportOnlyModules: tanstack route
export const Route = createFileRoute('/explore/')({
  component: () => (
    <MapLayout title="shared.navigation.explore">
      <Explore />
    </MapLayout>
  ),
});
